import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {ActiveVehicleActions, selectActiveVehicle, selectGarage} from '@ox/data';
import {GarageVehicles, Vehicle} from '@ox/typings';
import {VehiclePickerComponent} from '@ox/ui';
import {AutoDestroy, hasVehicleId, hasVin} from '@ox/utils';
import {isEmpty} from 'lodash';
import {combineLatest, map, Observable, Subject, takeUntil, tap, take} from 'rxjs';

@Component({
  selector: 'webu-vehicle-switcher',
  templateUrl: './vehicle-switcher.component.html'
})
export class VehicleSwitcherComponent {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Output() idSelected = new EventEmitter<string>();
  @Input() isModalClosable: boolean;
  public showVPModal: boolean;
  public vehiclePicker = VehiclePickerComponent;
  public vehicles: Vehicle[];
  public hasActive = false;
  public hasProfile = false;
  public make: string;
  public model: string;
  public vm$: Observable<{ garage: Vehicle[], activeVehicle: Vehicle | undefined }>;
  public activeVehicle$ = this.store.select<Vehicle | undefined>(selectActiveVehicle).pipe(tap((v: Vehicle | undefined) => this.canCloseModal(v)), tap((vehicle: Vehicle | undefined)=> this.removeVehicleParams(vehicle)));
  public garage$ = this.store.select<GarageVehicles>(selectGarage).pipe(
    takeUntil(this.destroy$),
    map((data) => {
      return data ? Array.from(data.values()) : [];
    }));

  public vpOutputs = {
    selectedVehicle: new EventEmitter(),
    closeModal: new EventEmitter()
  };
  public vpModalStyling = {
    class: 'v-picker',
    backdrop: true,
    ignoreBackdropClick: false,
    keyboard: true
  };

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.vm$ = combineLatest([this.activeVehicle$, this.garage$, this.route.queryParams])
      .pipe(map(([activeVehicle, garage, params]) => {
        if(activeVehicle) {
          this.make = this.getMake(activeVehicle);
          this.model = this.getModel(activeVehicle);
        }
        else if(garage.length == 1 && activeVehicle === null && isEmpty(params)) {
          this.store.dispatch(ActiveVehicleActions.update({vehicle: garage.at(0) as Vehicle}));
        }
        else if (activeVehicle === null && garage.length > 1 && (!isEmpty(params) && (params['vin'] || params['id'] || params['activeVin']))) {
          //show vp if vehicle in params is not in the garage
          const inGarage = (params['id']) ?
            hasVehicleId(garage, params['id'])
            : (params['vin'] ?? params['activeVin']) ?
              hasVin(garage, params['vin'] ?? params['activeVin'])
              : false;
          if (garage.length > 1 && !inGarage) this.openVPModal();
        } else {
          if (activeVehicle === null && garage.length > 1) this.openVPModal();
        }
        return {garage, activeVehicle};
      }));

    this.vpOutputs.selectedVehicle.pipe(
      takeUntil(this.destroy$),
      tap((vehicle: Vehicle) => {
        this.idSelected.emit(vehicle.vehicle_id);
        this.store.dispatch(ActiveVehicleActions.update({vehicle}));
        this.store.dispatch(ActiveVehicleActions.updateLastActive({vehicle}));
      })).subscribe();      
  }

  private canCloseModal(v?: Vehicle) {
    this.vpModalStyling.ignoreBackdropClick = this.isModalClosable ? !this.isModalClosable : !v;
    this.vpModalStyling.keyboard = this.isModalClosable ? this.isModalClosable : !!v;
  }

  public openVPModal(): void {
    if(!this.isAnyModalOpen()) {
      this.showVPModal = true;
    }
  }

  isAnyModalOpen(): boolean {
    return (document.querySelectorAll('.modal-content ox-vehicle-picker').length > 0);
  }

  /**
   * @description Removes vin/activeVin/id query params from url if active vehicle is selected and not on recalls mfe. 
   * Active vehicle is selected via `deep-linking` ngrx effect, which dispatches active vehicle based on url 
   * vehicle params. On recalls mfe, it is valid for vin in url to mismatch with current active vehicle.
   * @param activeVehicle
   */
  private removeVehicleParams(activeVehicle?: Vehicle) {
    if(activeVehicle && this.route.outlet !== 'recalls') {
      this.router.navigate([], {
        queryParams: {
          'id': null,
          'activeVin': null,
          'vin': null
        },
        queryParamsHandling: 'merge'
      });
    }
  }

  private getMake(vehicle: Vehicle) {
    return (vehicle.localeMakeName && vehicle.localeMakeName !== vehicle.make) ? vehicle.localeMakeName as string : vehicle.make as string;
  }

  private getModel(vehicle: Vehicle) {
    return (vehicle.localeModelName && vehicle.localeModelName !== vehicle.model) ? vehicle.localeModelName as string : vehicle.model  as string;
  }

}
